/**
 * VIKIBOX Google Analytics 4 (GA4) Behavioral & Lead Tracking Module
 * Version: 1.2.0
 *
 * Implements GA4 event mapping for user behavior, interactions, and form funnel:
 * - select_item (model card clicks, no hardcoded price)
 * - cta_click (key conversion button clicks with strict CTA enum)
 * - form_start (first interaction with a form, preserved across delayed consent)
 * - form_error (form validation errors, strictly without field values)
 * - generate_lead (verified server-side success with deduplication and RFC4122 v4 UUID)
 * - contact_click (WhatsApp, telephone, email links - zero PII)
 * - view_item (product & configuration module views with verified pricing)
 *
 * Privacy & GDPR Compliance:
 * - Strictly respects Shopify Customer Privacy API and Consent Mode v2 (Fail-Closed).
 * - Zero PII: no names, emails, phones, VINs, or freeform text sent to GA4.
 * - Deduplicated leads via sessionStorage & runtime memory fallback.
 * - Explicit send_to prevents routing to Google Ads or third-party tags.
 */
(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    define([], factory);
  } else if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.VikiboxGA4 = factory();
  }
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var VERSION = '1.2.0';
  var initialized = false;
  var UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  // Runtime memory caches
  var memoryDedup = {};
  var memoryFormStart = {};
  var memoryLeadIds = {};
  var memoryModelSelection = '';
  var memoryCategorySelection = '';

  // Strict CTA Allowlist Map
  var CTA_ENUM_MAP = {
    'modelle entdecken': 'cta_discover_models',
    'modell entdecken': 'cta_discover_model',
    'kompatibilität prüfen': 'cta_check_compatibility',
    'kompatibilitat prufen': 'cta_check_compatibility',
    'campingboxen ansehen': 'cta_campingboxen_ansehen',
    'campingbox konfigurieren': 'cta_configure_box',
    'individuelle vikibox planen': 'cta_plan_custom_box',
    'individuelle box anfragen': 'cta_custom_box_request',
    'jetzt anfragen': 'cta_request_now',
    'jetzt konfigurieren': 'cta_configure_now',
    'beratung anfordern': 'cta_request_consultation',
    'angebot anfordern': 'cta_request_quote',
    'whatsapp chat starten': 'cta_whatsapp',
    'zum angebot': 'cta_view_offer',
    'absenden': 'cta_submit_form',
    'anfrage senden': 'cta_send_inquiry',
    'viki go wählen': 'cta_select_viki_go',
    'viki go wahlen': 'cta_select_viki_go',
    'viki flex wählen': 'cta_select_viki_flex',
    'viki flex wahlen': 'cta_select_viki_flex',
    'viki max wählen': 'cta_select_viki_max',
    'viki max wahlen': 'cta_select_viki_max',
    'viki suv wählen': 'cta_select_viki_suv',
    'viki suv wahlen': 'cta_select_viki_suv'
  };

  /**
   * Safely accesses sessionStorage, probing capability to handle throwing storage
   */
  function getSessionStorage() {
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        var probe = '__vb_ss_probe__';
        window.sessionStorage.setItem(probe, '1');
        window.sessionStorage.removeItem(probe);
        return window.sessionStorage;
      }
    } catch (e) {}
    return null;
  }

  /**
   * Generates a standard RFC4122 v4 UUID
   */
  function generateUUID() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0;
      var v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Sanitizes string: removes control characters and trims length
   */
  function sanitizeString(val, maxLen) {
    if (val === null || val === undefined) return '';
    var clean = String(val).replace(/[\x00-\x1F\x7F]/g, '').trim();
    var limit = maxLen || 100;
    return clean.length > limit ? clean.substring(0, limit) : clean;
  }

  /**
   * Checks if analytics tracking is permitted under Shopify Customer Privacy API (Strict Fail-Closed)
   */
  function isAnalyticsAllowed() {
    // 1. Check dataLayer consent state if CMP pushed consent update (latest runtime command)
    if (typeof window !== 'undefined' && window.dataLayer) {
      for (var i = window.dataLayer.length - 1; i >= 0; i--) {
        var item = window.dataLayer[i];
        if (item && item[0] === 'consent' && item[2] && typeof item[2].analytics_storage !== 'undefined') {
          return item[2].analytics_storage === 'granted';
        }
      }
    }

    // 2. Check Shopify Customer Privacy API
    if (typeof window !== 'undefined' && window.Shopify && window.Shopify.customerPrivacy) {
      var cp = window.Shopify.customerPrivacy;
      try {
        if (typeof cp.analyticsProcessingAllowed === 'function') {
          return cp.analyticsProcessingAllowed() === true;
        }
        if (typeof cp.analyticsAllowed === 'function') {
          return cp.analyticsAllowed() === true;
        }
      } catch (e) {}
    }

    // Strict Fail-Closed default: no consent granted -> tracking disallowed
    return false;
  }

  /**
   * Dispatches event to Google Analytics via gtag with explicit send_to routing
   * Returns boolean indicating if event was successfully handed to gtag
   */
  function sendGA4Event(eventName, eventParams) {
    if (!isAnalyticsAllowed()) {
      return false;
    }
    if (typeof window === 'undefined') return false;

    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
    }

    var params = {};
    if (eventParams && typeof eventParams === 'object') {
      for (var key in eventParams) {
        if (Object.prototype.hasOwnProperty.call(eventParams, key)) {
          params[key] = eventParams[key];
        }
      }
    }

    var measurementId = (window.VIKIBOX_GA4_CONFIG && window.VIKIBOX_GA4_CONFIG.measurementId) || 'G-77Z4VEGE92';
    // Explicit send_to routing to prevent cross-talk with Google Ads or other destinations
    params.send_to = measurementId;

    // Check debug mode flag
    var isDebug = (window.VIKIBOX_GA4_CONFIG && window.VIKIBOX_GA4_CONFIG.debugMode) ||
                  (typeof window.location !== 'undefined' && (window.location.search.indexOf('vb_debug=1') !== -1 || window.location.search.indexOf('debug_mode=1') !== -1)) ||
                  (function () {
                    var s = getSessionStorage();
                    return s ? s.getItem('vb_debug_mode') === '1' : false;
                  })();
    if (isDebug) {
      params.debug_mode = true;
    }

    // Check internal traffic flag (permanent mobile/dynamic IP exclusion)
    var isInternal = (window.VIKIBOX_GA4_CONFIG && window.VIKIBOX_GA4_CONFIG.internalTraffic) ||
                     (typeof window.location !== 'undefined' && window.location.search.indexOf('vb_internal=1') !== -1) ||
                     (function () {
                       try {
                         return typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('vb_internal_traffic') === '1';
                       } catch (e) {
                         return false;
                       }
                     })();
    if (isInternal) {
      params.traffic_type = 'internal';
    }

    window.gtag('event', eventName, params);
    return true;
  }

  /**
   * Normalizes box model title
   */
  function normalizeModel(title) {
    if (!title) return 'none';
    var lower = String(title).toLowerCase().trim();
    if (lower === 'viki_go' || lower === 'viki go' || lower === 'go' || lower.indexOf('viki go') !== -1) return 'viki_go';
    if (lower === 'viki_flex' || lower === 'viki flex' || lower === 'flex' || lower.indexOf('viki flex') !== -1) return 'viki_flex';
    if (lower === 'viki_max' || lower === 'viki max' || lower === 'max' || lower.indexOf('viki max') !== -1) return 'viki_max';
    if (lower === 'viki_suv' || lower === 'viki suv' || lower === 'suv' || lower.indexOf('viki suv') !== -1) return 'viki_suv';
    if (lower === 'custom' || lower.indexOf('individuell') !== -1) return 'custom';
    return 'none';
  }

  /**
   * Sanitizes and maps CTA button texts to strict enums
   * Falls back strictly to 'cta_unknown' instead of generating arbitrary slugs
   */
  function sanitizeCtaName(rawText, el) {
    if (el && typeof el.getAttribute === 'function') {
      var explicitCta = el.getAttribute('data-cta');
      if (explicitCta && /^cta_[a-z0-9_]+$/.test(explicitCta)) {
        return explicitCta;
      }
    }
    if (!rawText) return 'cta_unknown';
    var cleaned = rawText.replace(/→|↵|[\r\n\t]/g, '').trim().toLowerCase();
    cleaned = cleaned.replace(/ä/g, 'a').replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ß/g, 'ss');
    if (CTA_ENUM_MAP[cleaned]) {
      return CTA_ENUM_MAP[cleaned];
    }
    return 'cta_unknown';
  }

  /**
   * Determines placement context from DOM node
   */
  function getPlacement(el) {
    if (!el) return 'unknown';
    if (el.closest('.vb-hero')) return 'hero';
    if (el.closest('.vb-models') || el.closest('#modelle')) return 'models_grid';
    if (el.closest('.vb-individual')) return 'custom_order_teaser';
    if (el.closest('#header-group') || el.closest('header')) return 'header';
    if (el.closest('footer') || el.closest('#footer-group')) return 'footer';
    if (el.closest('#vb-exit-modal')) return 'exit_modal';
    if (el.closest('.vb-compatibility-form') || el.closest('#VikiboxCompatibilityForm')) return 'compatibility_form';
    if (el.closest('.vb-order__form') || el.closest('#VikiboxOrderForm')) return 'custom_order_form';
    return 'content';
  }

  // Strict Allowlist for Box Models
  var ALLOWED_BOX_MODELS = {
    'viki_go': true,
    'viki_flex': true,
    'viki_max': true,
    'viki_suv': true,
    'custom': true
  };

  // Strict Allowlist for Vehicle Categories
  var ALLOWED_VEHICLE_CATEGORIES = {
    'hochdachkombi': 'Hochdachkombi',
    'minivan': 'Minivan',
    'bus_transporter': 'Bus_Transporter',
    'transporter': 'Bus_Transporter',
    'suv': 'SUV',
    'kombi': 'Kombi',
    'default': 'default'
  };

  /**
   * Normalizes vehicle category to strict allowlist, returns 'unknown' for arbitrary free text
   */
  function normalizeVehicleCategory(val) {
    if (!val) return 'default';
    var clean = String(val).trim().toLowerCase();
    if (ALLOWED_VEHICLE_CATEGORIES[clean]) {
      return ALLOWED_VEHICLE_CATEGORIES[clean];
    }
    return 'unknown';
  }

  /**
   * Caches user selections during their journey for recovery on the server success page
   * Strictly respects privacy: NEVER caches anything if analytics is disallowed.
   * Strictly respects allowlists: only caches known valid box models and vehicle categories, NEVER raw user free text.
   */
  function cacheUserSelections(model, category) {
    if (!isAnalyticsAllowed()) return;
    var ss = getSessionStorage();
    if (model && ALLOWED_BOX_MODELS[model]) {
      memoryModelSelection = model;
      if (ss) { try { ss.setItem('vb_last_selected_model', model); } catch (e) {} }
    }
    if (category) {
      var normCat = normalizeVehicleCategory(category);
      if (normCat !== 'unknown') {
        memoryCategorySelection = normCat;
        if (ss) { try { ss.setItem('vb_last_vehicle_category', normCat); } catch (e) {} }
      }
    }
  }

  /**
   * Clears temporary selection caches after lead dispatch
   */
  function clearUserSelectionCaches() {
    memoryModelSelection = null;
    memoryCategorySelection = null;
    var ss = getSessionStorage();
    if (ss) {
      try {
        ss.removeItem('vb_last_selected_model');
        ss.removeItem('vb_last_vehicle_category');
      } catch (e) {}
    }
  }

  /**
   * Resolves verified stable lead_id (RFC4122 v4 UUID) from Attribution module or session
   * Priority: 1. Active pending lead -> 2. Stored server success -> 3. Attribution helper -> 4. Stable memory fallback
   * If requirePersistent is true and storage is unavailable/absent, returns null to avoid phantom leads on reloads
   */
  function getVerifiedLeadId(formType, requirePersistent) {
    var fType = formType || 'compatibility';
    var ss = getSessionStorage();

    // 1. Check pending lead ID first (a fresh form submission takes precedence)
    if (ss) {
      try {
        var pending = ss.getItem('vikibox_pending_lead_id:' + fType);
        if (pending && UUID_REGEX.test(pending)) {
          memoryLeadIds[fType] = pending;
          return pending;
        }
      } catch (e) {}
    }

    // 2. Check server success storage written by VikiboxAttribution (for F5 / reload of confirmation)
    if (ss) {
      try {
        var rawSuccess = ss.getItem('vikibox_server_success_lead:' + fType);
        if (rawSuccess) {
          if (rawSuccess.charAt(0) === '{') {
            var parsed = JSON.parse(rawSuccess);
            if (parsed && parsed.lead_id && UUID_REGEX.test(parsed.lead_id)) {
              memoryLeadIds[fType] = parsed.lead_id;
              return parsed.lead_id;
            }
          } else if (UUID_REGEX.test(rawSuccess)) {
            memoryLeadIds[fType] = rawSuccess;
            return rawSuccess;
          }
        }
      } catch (e) {}
    }

    // If persistent storage is strictly required and no stored lead was found, do NOT call any generator
    if (requirePersistent) {
      return null;
    }

    // 3. Check VikiboxAttribution helper if available in runtime (only for active interactions / non-persistent mode)
    if (typeof window !== 'undefined' && window.VikiboxAttribution && typeof window.VikiboxAttribution.getOrCreateLeadId === 'function') {
      try {
        var attrLeadId = window.VikiboxAttribution.getOrCreateLeadId(fType);
        if (attrLeadId && UUID_REGEX.test(attrLeadId)) {
          memoryLeadIds[fType] = attrLeadId;
          return attrLeadId;
        }
      } catch (e) {}
    }

    // 4. Stable session memory fallback: ensure repeated calls in same runtime return the EXACT SAME UUID
    if (memoryLeadIds[fType]) {
      return memoryLeadIds[fType];
    }

    var newId = generateUUID();
    memoryLeadIds[fType] = newId;
    if (ss) {
      try {
        ss.setItem('vikibox_pending_lead_id:' + fType, newId);
      } catch (e) {}
    }
    return newId;
  }

  /**
   * Tracks model selection / card click
   */
  function trackSelectModel(cardEl) {
    if (!cardEl) return;
    var heading = cardEl.querySelector('h3');
    var rawTitle = heading ? heading.textContent.trim() : '';
    var modelSlug = normalizeModel(rawTitle);

    cacheUserSelections(modelSlug, null);

    var itemObj = {
      item_id: modelSlug,
      item_name: rawTitle || modelSlug,
      item_category: 'Campingbox'
    };

    // Data fidelity: only include verified price if explicitly specified on DOM
    var priceAttr = cardEl.getAttribute('data-price') || (cardEl.querySelector('[data-price]') && cardEl.querySelector('[data-price]').getAttribute('data-price'));
    if (priceAttr) {
      var num = parseFloat(priceAttr);
      if (!isNaN(num) && num > 0) {
        itemObj.price = num;
        itemObj.currency = 'EUR';
      }
    }

    sendGA4Event('select_item', {
      item_list_name: 'homepage_boxes',
      items: [itemObj]
    });
  }

  /**
   * Tracks CTA button clicks
   */
  function trackCtaClick(buttonEl) {
    if (!buttonEl) return;
    var rawText = buttonEl.textContent.replace(/→|↵|[\r\n\t]/g, '').trim();
    var ctaEnum = sanitizeCtaName(rawText, buttonEl);
    var placement = getPlacement(buttonEl);
    var model = '';

    var parentModel = buttonEl.closest('.vb-model');
    if (parentModel) {
      var h3 = parentModel.querySelector('h3');
      if (h3) model = normalizeModel(h3.textContent);
    }

    var params = {
      cta_name: ctaEnum,
      placement: placement
    };
    if (model && model !== 'none') {
      params.box_model = model;
    }

    sendGA4Event('cta_click', params);
  }

  /**
   * Tracks contact clicks (WhatsApp, Phone, Email) - Zero PII
   */
  function trackContactClick(linkEl) {
    if (!linkEl) return;
    var href = (linkEl.getAttribute('href') || '').toLowerCase();
    var method = '';

    if (href.indexOf('wa.me') !== -1 || href.indexOf('whatsapp.com') !== -1) {
      method = 'whatsapp';
    } else if (href.indexOf('tel:') === 0) {
      method = 'phone';
    } else if (href.indexOf('mailto:') === 0) {
      method = 'email';
    } else {
      return;
    }

    var placement = getPlacement(linkEl);
    sendGA4Event('contact_click', {
      contact_method: method,
      placement: placement
    });
  }

  /**
   * Checks if lead has already been processed
   */
  function isLeadProcessed(leadId) {
    if (!leadId || leadId === 'unknown') return false;
    var storage = getSessionStorage();
    if (storage) {
      try {
        if (storage.getItem('vb_ga4_lead_' + leadId)) return true;
      } catch (e) {}
    }
    return Boolean(memoryDedup[leadId]);
  }

  /**
   * Marks lead as processed in sessionStorage and runtime memory strictly AFTER dispatch
   */
  function markLeadProcessed(leadId) {
    if (!leadId || leadId === 'unknown') return;
    memoryDedup[leadId] = true;
    var storage = getSessionStorage();
    if (storage) {
      try {
        storage.setItem('vb_ga4_lead_' + leadId, '1');
      } catch (e) {}
    }
  }

  /**
   * Tracks form_start (fired once per form per session, state saved strictly AFTER successful dispatch)
   * Returns boolean indicating if event was successfully dispatched
   */
  function trackFormStart(formType) {
    if (!formType) return false;
    var storage = getSessionStorage();
    var key = 'vb_ga4_form_start_' + formType;
    if ((storage && storage.getItem(key)) || memoryFormStart[formType]) {
      return false; // Already tracked in this session
    }

    var dispatched = sendGA4Event('form_start', {
      form_type: formType
    });

    if (dispatched) {
      memoryFormStart[formType] = true;
      if (storage) {
        try { storage.setItem(key, '1'); } catch (e) {}
      }
    }
    return dispatched;
  }

  /**
   * Tracks form_error without any user input or PII
   */
  function trackFormError(formType, errorType) {
    if (!formType) return;
    sendGA4Event('form_error', {
      form_type: formType,
      error_type: errorType || 'validation_error'
    });
  }

  /**
   * Tracks confirmed lead generation upon server-verified success
   * State is marked processed strictly AFTER successful dispatch
   */
  function trackLeadGenerated(formType, payload) {
    if (!formType) return false;
    var leadId = (payload && payload.lead_id) || getVerifiedLeadId(formType);

    if (leadId && isLeadProcessed(leadId)) {
      return false; // Suppress duplicate firing on F5 / refresh
    }

    var model = 'none';
    if (payload && payload.box_model) {
      model = normalizeModel(payload.box_model);
    }

    var eventParams = {
      form_type: formType,
      box_model: model,
      lead_id: leadId
    };

    // Separate vehicle_category from box_model
    if (payload && payload.vehicle_category) {
      eventParams.vehicle_category = normalizeVehicleCategory(payload.vehicle_category);
    }

    var dispatched = sendGA4Event('generate_lead', eventParams);
    if (dispatched && leadId) {
      markLeadProcessed(leadId);
    }
    return dispatched;
  }

  var memoryProductViewed = {};

  /**
   * Tracks view_item for product modules or product template views (deduplicated per page view)
   */
  function checkProductView() {
    if (!isAnalyticsAllowed()) return;
    if (typeof document === 'undefined') return;

    var prodEl = document.querySelector('viki-product-modules[data-base-title], [data-product-id], .template-product');
    if (!prodEl) return;

    var title = prodEl.getAttribute('data-base-title') || (document.querySelector('h1') ? document.querySelector('h1').textContent.trim() : 'VIKIBOX');
    var pId = prodEl.getAttribute('data-product-id') || normalizeModel(title);

    if (memoryProductViewed[pId]) {
      return; // Already tracked on this page view
    }

    var itemObj = {
      item_id: pId,
      item_name: title,
      item_category: 'Campingbox'
    };

    var rawPrice = prodEl.getAttribute('data-base-price');
    if (rawPrice) {
      var num = parseFloat(rawPrice);
      if (!isNaN(num) && num > 0) {
        if (num > 10000) num = num / 100; // Shopify money format in cents
        itemObj.price = num;
        itemObj.currency = 'EUR';
      }
    }

    var sent = sendGA4Event('view_item', {
      items: [itemObj]
    });
    if (sent) {
      memoryProductViewed[pId] = true;
    }
  }

  /**
   * Sets up global click and interaction listeners
   */
  function setupEventListeners() {
    if (typeof document === 'undefined') return;

    // Delegated click listener
    document.addEventListener('click', function (e) {
      var target = e.target;
      if (!target) return;

      // 1. Contact links (WhatsApp, Phone, Email)
      var contactLink = target.closest('a[href^="tel:"], a[href^="mailto:"], a[href*="wa.me"], a[href*="whatsapp.com"]');
      if (contactLink) {
        trackContactClick(contactLink);
        return;
      }

      // 2. Model card click / selection
      var modelCard = target.closest('.vb-model');
      var isDirectLink = target.closest('a');
      if (modelCard && isDirectLink && isDirectLink.classList.contains('vb-text-link')) {
        trackSelectModel(modelCard);
        trackCtaClick(isDirectLink);
        return;
      }
      if (modelCard && !isDirectLink) {
        trackSelectModel(modelCard);
        return;
      }

      // 3. CTA Buttons
      var ctaBtn = target.closest('.vb-btn, .vb-exit-modal__cta, .vb-individual a, .vb-text-link');
      if (ctaBtn) {
        trackCtaClick(ctaBtn);
      }
    }, true);

    // Form start listeners (first interaction, with delayed consent preservation)
    function attachFormListeners(formEl, formType) {
      if (!formEl) return;
      var hasInteracted = false;

      function preserveDebugReturnTo() {
        var isDbg = (window.VIKIBOX_GA4_CONFIG && window.VIKIBOX_GA4_CONFIG.debugMode) ||
                    (typeof window.location !== 'undefined' && window.location.search.indexOf('vb_debug=1') !== -1) ||
                    (function () {
                      var s = getSessionStorage();
                      return s ? s.getItem('vb_debug_mode') === '1' : false;
                    })();
        if (!isDbg) return;
        var returnInput = formEl.querySelector('input[name="return_to"]');
        if (returnInput && returnInput.value && returnInput.value.indexOf('vb_debug=1') === -1) {
          var val = returnInput.value;
          var hashIdx = val.indexOf('#');
          var hash = hashIdx !== -1 ? val.substring(hashIdx) : '';
          var base = hashIdx !== -1 ? val.substring(0, hashIdx) : val;
          var sep = base.indexOf('?') !== -1 ? '&' : '?';
          returnInput.value = base + sep + 'vb_debug=1' + hash;
        }
      }
      preserveDebugReturnTo();

      function onFirstInteraction() {
        preserveDebugReturnTo();
        if (!hasInteracted) {
          var dispatched = trackFormStart(formType);
          if (dispatched) {
            hasInteracted = true;
          }
        }
      }

      formEl.addEventListener('focusin', onFirstInteraction, { passive: true });
      formEl.addEventListener('input', onFirstInteraction, { passive: true });
      formEl.addEventListener('change', function (e) {
        onFirstInteraction();
        if (!isAnalyticsAllowed()) return;
        var target = e.target;
        if (!target || !target.name) return;
        // Only cache category if element is explicitly a category select, NEVER free-text brand or model
        if (target.name === 'contact[Fahrzeugkategorie]' || (target.dataset && target.dataset.vehicleCategory)) {
          var cat = target.dataset.vehicleCategory || target.value;
          cacheUserSelections(null, cat);
        }
      }, { passive: true });

      // Form validation error listener
      formEl.addEventListener('invalid', function () {
        trackFormError(formType, 'required_field_missing');
      }, true);
    }

    // Attach to compatibility form
    var compatForm = document.querySelector('#VikiboxCompatibilityForm, form[action*="kompatibilitaet"]');
    if (compatForm) {
      attachFormListeners(compatForm, 'compatibility');
    }

    // Attach to custom order form
    var orderForm = document.querySelector('#VikiboxOrderForm, form[action*="individuelle-vikibox-planen"]');
    if (orderForm) {
      attachFormListeners(orderForm, 'custom_order');
    }

    // Attach to contact form
    var contactForm = document.querySelector('form[action*="/contact"]');
    if (contactForm && contactForm !== compatForm && contactForm !== orderForm) {
      attachFormListeners(contactForm, 'contact_general');
    }
  }

  /**
   * Checks for server-rendered success confirmation on page load
   */
  function checkServerSuccess() {
    if (typeof document === 'undefined') return;
    var ss = getSessionStorage();

    // Check custom order server success
    var customSuccess = document.querySelector('#VikiboxOrderSuccess[data-vb-server-success="custom_order"], [data-vb-server-success="custom_order"]');
    if (customSuccess) {
      var leadId = getVerifiedLeadId('custom_order', true);
      if (!leadId) {
        return; // Avoid phantom lead without verified storage/attribution ID
      }
      var vCat = customSuccess.getAttribute('data-vehicle-category') || 'default';
      var customDispatched = trackLeadGenerated('custom_order', {
        lead_id: leadId,
        box_model: 'custom',
        vehicle_category: normalizeVehicleCategory(vCat)
      });
      if (customDispatched) {
        clearUserSelectionCaches();
      }
      return;
    }

    // Check compatibility server success
    var compatSuccess = document.querySelector('#VikiboxCompatibilitySuccess[data-vb-server-success="compatibility"], [data-vb-server-success="compatibility"]');
    if (compatSuccess) {
      var compLeadId = getVerifiedLeadId('compatibility', true);
      if (!compLeadId) {
        return; // Avoid phantom lead without verified storage/attribution ID
      }
      var compModel = compatSuccess.getAttribute('data-box-model');
      if (!compModel) {
        compModel = memoryModelSelection || (ss && ss.getItem('vb_last_selected_model')) || 'none';
      }
      var compCat = compatSuccess.getAttribute('data-vehicle-category') || 'default';
      var compDispatched = trackLeadGenerated('compatibility', {
        lead_id: compLeadId,
        box_model: compModel ? normalizeModel(compModel) : 'none',
        vehicle_category: normalizeVehicleCategory(compCat)
      });
      if (compDispatched) {
        clearUserSelectionCaches();
      }
    }
  }

  /**
   * Initializes GA4 tracking module
   */
  function init() {
    if (initialized) return;
    initialized = true;

    setupEventListeners();
    checkServerSuccess();
    checkProductView();

    // Listen for visitorConsentCollected and ga4_ready events
    if (typeof document !== 'undefined') {
      document.addEventListener('visitorConsentCollected', function (event) {
        var allowed = Boolean(event && event.detail && event.detail.analyticsAllowed);
        if (allowed) {
          checkServerSuccess();
          checkProductView();
        }
      });
      window.addEventListener('vikibox:ga4_ready', function () {
        checkServerSuccess();
        checkProductView();
      });
    }
  }

  // Auto-init on DOMContentLoaded or immediately if ready
  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        init();
      });
    } else {
      init();
    }
  }

  return {
    version: VERSION,
    init: init,
    sendEvent: sendGA4Event,
    isAnalyticsAllowed: isAnalyticsAllowed,
    getVerifiedLeadId: getVerifiedLeadId,
    trackLeadGenerated: trackLeadGenerated,
    trackFormStart: trackFormStart,
    trackFormError: trackFormError,
    trackSelectModel: trackSelectModel,
    trackCtaClick: trackCtaClick,
    trackContactClick: trackContactClick,
    checkServerSuccess: checkServerSuccess,
    checkProductView: checkProductView,
    sanitizeCtaName: sanitizeCtaName
  };
}));
